import React, { useState, useRef } from "react";
import { Button, TextField, MenuItem, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ShiftTask } from "@/logic/api";
import type { ScanningState } from "../hooks/useScanningFlow";

interface ManualInputProps {
  state: ScanningState;
  tasks: ShiftTask[];
  expectKeyScanBeforeLocation?: boolean;
  onScan: (value: string) => void;
  onSelectLocation: () => void;
  onTaskSelected: (taskKey: string) => void;
  onCancel?: () => void;
}

const ManualInput: React.FC<ManualInputProps> = ({
  state,
  tasks,
  expectKeyScanBeforeLocation = false,
  onScan,
  onSelectLocation,
  onTaskSelected,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [barcode, setBarcode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    if (barcode.trim().length > 0) {
      onScan(barcode.trim());
      setBarcode("");
    }
  };

  //  normal flow: idle = input + location. user_selected = input + location + task dropdown
  // reversed flow: idle = input + task dropdown only. task_selected = input + location + cancel
  const showLocationButton =
    (state.status === "idle" && !expectKeyScanBeforeLocation) ||
    state.status === "task_selected" ||
    state.status === "location_selected" ||
    state.status === "user_selected" ||
    state.status === "key_scanned";

  const showTaskDropdown =
    (state.status === "user_selected" && tasks.length > 0) ||
    (expectKeyScanBeforeLocation && state.status === "idle" && tasks.length > 0);

  const showCancelButton =
    (state.status === "task_selected" || state.status === "key_scanned") && onCancel;

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
    >
      <TextField
        focused
        inputRef={inputRef}
        inputProps={{ autoComplete: "off", name: "scanning-input" }}
        sx={{
          flex: 1,
          minWidth: { xs: "100%", sm: 250 },
        }}
        type="search"
        size="small"
        label={t("scanning.manual.inputLabel")}
        placeholder={t("scanning.manual.inputPlaceholder")}
        variant="outlined"
        value={barcode}
        onChange={(e) => setBarcode(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
      />

      {showLocationButton && (
        <Button
          onClick={onSelectLocation}
          variant="contained"
          color={
            state.status === "idle" ||
              state.status === "key_scanned" ||
              state.status === "task_selected"
              ? "secondary"
              : "primary"
          }
          sx={{ height: 40 }}
        >
          {state.status === "idle" ||
            state.status === "key_scanned" ||
            state.status === "task_selected"
            ? t("scanning.manual.selectLocation")
            : t("scanning.manual.changeLocation")}
        </Button>
      )}

      {showTaskDropdown && (
        <TextField
          select
          size="small"
          label={t("scanning.manual.selectTask")}
          value=""
          onChange={(e) => {
            if (e.target.value) {
              onTaskSelected(e.target.value);
            }
          }}
          sx={{ minWidth: { xs: "100%", sm: 200 } }}
        >
          {tasks.map((task) => (
            <MenuItem key={task.key} value={task.key}>
              {task.title}
            </MenuItem>
          ))}
        </TextField>
      )}

      {showCancelButton && (
        <Button
          onClick={onCancel}
          variant="outlined"
          color="secondary"
          sx={{ height: 40 }}
        >
          {t("scanning.manual.cancel")}
        </Button>
      )}
    </Stack>
  );
};

export default ManualInput;
